import React, {useRef, useState} from "react";
import {Button, Col, DatePicker, Drawer, Form, Input, Radio, Row, Select, Space, Typography} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import PropTypes from "prop-types";

import EngageCurrency from "./EngageCurrency";
import InputMoney from "./InputMoney";
import TaxCheckboxesInForm from "./TaxCheckboxesInForm";
import {createCharge, handleChargePreTaxCalc, handleChargePostTaxCalc} from "../../helpers/billing";
import {messageType, showMessages} from "../../helpers/message";
import {getAccountMembershipNumberPerson, getActiveAccount, getActiveMembership} from "../../lib/activeMember";
import {userHasRole} from "../../lib/role";
import {dateFormat} from "../../lib/time";
import {chargeCreatePostTaxDisclaimer,
        chargeCreateSuccessMessage,
        loadAccountInfoErrorText,
        greaterThanOrEqualZero} from "../../helpers/text";

import "./ChargeCreateEditDrawer.less";

const {Option} = Select;

const ChargeCreateDrawer = ({taxes, refreshBalances, refreshCharges}) => {
    //region State/Variable setup

    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [openDrawer, setOpenDrawer] = useState(false);
    const [preTaxAmount, setPreTaxAmount] = useState(null);
    const [postTaxAmount, setPostTaxAmount] = useState(null);
    const [sumOfTaxes, setSumOfTaxes] = useState(null);
    const isOwner = userHasRole('user', 'owner');
    const mobileMode = window.innerWidth <= 768;

    // We need to use ref instead of state to persist the values from the input
    // and not reset to initialState on any state update.
    const preTaxAmountRef = useRef(null);
    const postTaxAmountRef = useRef(null);
    const taxesApplied = useRef({});

    // We need to know what the last amount input field was changed
    // to determine which one to dynamically update on tax checkboxes ticks.
    const lastAmountInputFocused = useRef({});
    const [form] = Form.useForm();

    //endregion

    //region Event Handlers

    // Event handlers to show/hide the drawer component.
    const showDrawer = () => {
        setOpenDrawer(true);
    };

    const hideDrawer = () => {
        setPreTaxAmount(null);
        setPostTaxAmount(null);
        setSumOfTaxes(null);
        preTaxAmountRef.current = null;
        postTaxAmountRef.current = null;
        taxesApplied.current = {};
        form.resetFields();
        setOpenDrawer(false);
    };

    // Within each of the nested calc handlers below we need to update the inverse pre/post tax amount.
    // Then refer to the taxesApplied ref to see which taxes checkboxes have been checked.
    // Then the taxes prop to apply the tax rates or inverse them. Reminder we purposely use ref AND state for the amounts.

    // Event handler for the taxes checkboxes being checked or unchecked,
    // sets the taxesApplied ref. And fires the pre/post tax event handlers to calculate taxes.
    const handleTaxesApplied = (e) => {
        const {value, checked} = e.target;
        taxesApplied.current[value] = checked;

        // We only want to fire one event handler at a time, when the tax checkboxes are checked or unchecked.
        // Determine based off the last focused input box.
        if (lastAmountInputFocused.current === 'preTaxAmount'){
            handleChargePreTaxCalc(undefined, preTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused);
        } else if (lastAmountInputFocused.current === 'postTaxAmount') {
            handleChargePostTaxCalc(undefined, postTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused);
        }
    }
    //endregion

    //region API functionality

    const handleFinish = async (values) => {
        const abortSignal = abortSignalRef.current;

        const amount = preTaxAmount;
        let membership_id;

        // If staff is not representing an owner fetch active membership id, otherwise use the form item.
        if (!isOwner) {
            membership_id = getActiveMembership().id;
        } else {
            // Using string null to prevent linter from throwing warning.
            membership_id = values['Belongs to'] !== 'null' ? values['Belongs to'] : null;
        }
        const chargedAt = values['Date'].format('YYYY-MM-DD HH:mm:ssZZ');
        const type = values['Category'];
        const name = values['Name'];
        const taxes = values['Taxes'];

        const data = {chargedAt, amount, name, type, taxes};

        try {
            setLoading(true)

            let account = getActiveAccount();
            if (account === null) {
                throw new Error(loadAccountInfoErrorText);
            }

            await createCharge(account.id, membership_id, data, abortSignal);
            showMessages(chargeCreateSuccessMessage, messageType.success);

            setLoading(false);
            hideDrawer();
            refreshBalances(true);
            refreshCharges();
        } catch (error) {
            showMessages(error.message, messageType.error)
            setLoading(false);
        }
    }

    //endregion

    return (
        <>
            <Button className="add-charge-btn" type="primary" icon={<PlusOutlined />} onClick={showDrawer}>
                Add New
            </Button>
            <Drawer className="charge-create-edit-drawer"
                    width={mobileMode ? "100%" : "45%"}
                    title="Add One-Time Charge"
                    placement="right"
                    closable={true}
                    onClose={hideDrawer}
                    open={openDrawer}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="charge-create"
                    onFinish={handleFinish}
                >
                {isOwner ?
                    <Row>
                        <Col>
                            <Form.Item
                                name="Belongs to"
                                label="This charge belongs to: "
                                rules={[{required: true}]}
                            >
                                <Radio.Group>
                                    <Space direction="vertical">
                                        <Radio value={getActiveMembership().id}>{getAccountMembershipNumberPerson()}</Radio>
                                        <Radio value="null">No particular member</Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                : null
                }
                <Row className="full-width-row">
                    <Col>
                        <Form.Item
                            name="Date"
                            label="Date: "
                            rules={[{type: 'object', required: true}]}
                        >
                            <DatePicker
                                className="full-width-date-picker"
                                placeholder="DD MM YYYY"
                                format={dateFormat}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row className="full-width-row">
                    <Col>
                        <Form.Item
                            name="Category"
                            label="Charge Category: "
                            rules={[{type: 'string', required: true}]}
                        >
                        <Select placeholder="Please select">
                            <Option value="adjustment">Adjustment</Option>
                            <Option value="fee">Fee</Option>
                            <Option value="fine">Fine</Option>
                            <Option value="membership">Membership</Option>
                        </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row className="full-width-row">
                    <Col>
                        <Form.Item name="Name" label="Charge Name: ">
                            <Input placeholder="Type here" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            name="Amount"
                            label="Amount (before tax): "
                            rules={[{type: 'number', required: !preTaxAmount, min: 0}]}
                            help={greaterThanOrEqualZero}
                            validateStatus={preTaxAmount ? (preTaxAmount >= 0.00 ? '' : 'error') : ''}
                        >
                            <InputMoney amount={preTaxAmount}
                                        onChange={(e) => handleChargePreTaxCalc(e, preTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                                                                                taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused)
                                        }
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <TaxCheckboxesInForm taxes={taxes} handleTaxesApplied={handleTaxesApplied} disabled={false} />
                <Row>
                    <Col span={24}>
                        <Form.Item
                            name="AmountAfterTax"
                            label="Total Amount (after tax): "
                            extra={chargeCreatePostTaxDisclaimer}
                            rules={[{type: 'number', min: 0}]}
                            help={greaterThanOrEqualZero}
                            validateStatus={postTaxAmount ? (postTaxAmount >= 0.00 ? '' : 'error') : ''}
                        >
                            <InputMoney amount={postTaxAmount}
                                        onChange={(e) => handleChargePostTaxCalc(e, postTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                                                                                 taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused)
                                        }
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <div className="subtotal-container">
                    <Row justify="space-between">
                        <Col><Typography.Text strong>Subtotal</Typography.Text></Col>
                        <Col><EngageCurrency amount={preTaxAmount ? parseFloat(preTaxAmount) : 0}/></Col>
                    </Row>
                    <Row justify="space-between">
                        <Col><Typography.Text strong>Taxes</Typography.Text></Col>
                        <Col><EngageCurrency amount={sumOfTaxes ? sumOfTaxes : 0}/></Col>
                    </Row>
                    <Row justify="space-between">
                        <Col><Typography.Title level={5} className="subtotal-label">Total Cost:</Typography.Title></Col>
                        <Col>
                            <Typography.Title level={5} className="subtotal-amount">
                                <EngageCurrency
                                    amount={postTaxAmount ? parseFloat(postTaxAmount) : 0}
                                />
                            </Typography.Title>
                        </Col>
                    </Row>
                </div>
                <Row>
                    <Button className="charge-create-edit-cancel-btn" onClick={hideDrawer}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={loading}>Add Charge</Button>
                </Row>
                </Form>
            </Drawer>
        </>
    )
}

export default ChargeCreateDrawer;

ChargeCreateDrawer.propTypes = {
    taxes: PropTypes.array.isRequired,
    refreshBalances: PropTypes.func.isRequired,
    refreshCharges: PropTypes.func.isRequired,
}
